import type { SurveyAttribute, SurveyScoresByCategoryXAttributes } from '@engagement-web-app/common';
import {
    CategoryScale,
    type ChartData,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { formatNumber, type MetricOption, metricOptions } from '@/utils';
import { customCanvasBackgroundColorOptions, customCanvasBackgroundColorPlugin } from '@/utils/chart';
import { AttributeFilter, AttributeFilterSkeleton } from './AttributeFilter';

ChartJS.register(CategoryScale, LinearScale, LineController, LineElement, PointElement, Title, Tooltip, Legend);

// Colors assigned per attribute index (excluding 全体 which is always black/dotted)
const attributeColors = [
    'rgb(220, 60, 60)', // red
    'rgb(30, 100, 210)', // blue
    'rgb(30, 150, 60)', // green
    'rgb(30, 170, 200)', // cyan
    'rgb(220, 150, 30)', // orange
    'rgb(190, 185, 0)', // yellow
    'rgb(150, 60, 200)', // purple
];

// Sentinel label used to hide the y=50 baseline from legend and tooltip
const BASELINE_LABEL = '__baseline__';

type SurveyCategoryXAttributesComparisonChartProps = {
    /** `null` while the current filter selection is (re)loading. */
    scores: SurveyScoresByCategoryXAttributes | null;
    /** `null` while still loading. Purely used for further filtering — not required for the first render. */
    attributeSettings: SurveyAttribute[] | null;
    selectedAttributeOptionIds: string[];
    onSelectedAttributeOptionIdsChange: (selectedAttributeOptionIds: string[]) => void;
    /** True while `scores` is being (re)fetched for the current filter. */
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
};

type AttributeProductScores = NonNullable<
    SurveyScoresByCategoryXAttributes['categories'][number]['attributes']
>[number];

/**
 * Size the chart is drawn at when printing. Fixed rather than measured: the
 * container still reports the on-screen layout while the print event runs, and
 * the chosen page orientation is not observable from script. Narrow enough for
 * portrait, so it holds for both.
 */
const PRINT_CHART_SIZE = { width: 600, height: 300 };

/**
 * Pure composition: `attributeSettings` and `scores` are independent loading
 * states (the former loads once, the latter reloads on every filter change), so
 * each half buffers its own rather than one gate covering both. Both only ever
 * buffer to a skeleton once, on true first load — the underlying queries keep the
 * previous filter's data around during a refetch, so `isFetching` (not a null
 * `scores`/`attributeSettings`) drives the overlay below for that case.
 */
export function SurveyCategoryXAttributesComparisonChart({
    scores,
    attributeSettings,
    selectedAttributeOptionIds,
    onSelectedAttributeOptionIdsChange,
    isFetching,
    isError,
    onRetry,
}: SurveyCategoryXAttributesComparisonChartProps) {
    const [selectedMetric, setSelectedMetric] = useState<MetricOption>('di');

    if (isError) {
        return <ErrorOverlay active onRetry={onRetry} message="カテゴリー比較 - 属性別の取得に失敗しました。" />;
    }

    return (
        <Section>
            <SectionHeader
                title="カテゴリー比較 - 属性別"
                description="カテゴリーごとのスコアを、全体と選択した属性グループとで比較します。指標は DI と偏差値を切り替えられます。対象の回答者が 3 人未満の属性グループは、スコアが表示されません。"
            />

            {/**
             * Putting the overlay here instead of in ChartCanvas:
             * 1. Avoids further prop drilling
             * 2. TODO: For some reason, wrapping the chart with any element affects its size
             * The tradeoff is that the filters will be out of reach while a query is in flight.
             */}
            <Skeleton.Overlay active={isFetching}>
                <Flex direction="column" gap="6" align="start">
                    <div>
                        <Select
                            value={selectedMetric}
                            onChange={(e) => setSelectedMetric(e.target.value as MetricOption)}
                        >
                            {metricOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>

                    {attributeSettings ? (
                        <AttributeFilterSection
                            attributeSettings={attributeSettings}
                            selectedAttributeOptionIds={selectedAttributeOptionIds}
                            onSelectedAttributeOptionIdsChange={onSelectedAttributeOptionIdsChange}
                        />
                    ) : (
                        <AttributeFilterSkeleton />
                    )}

                    {scores ? <ChartCanvas scores={scores} selectedMetric={selectedMetric} /> : <ChartCanvasSkeleton />}
                </Flex>
            </Skeleton.Overlay>
        </Section>
    );
}

type AttributeFilterSectionProps = {
    attributeSettings: SurveyAttribute[];
    selectedAttributeOptionIds: string[];
    onSelectedAttributeOptionIdsChange: (ids: string[]) => void;
};

function AttributeFilterSection({
    attributeSettings,
    selectedAttributeOptionIds,
    onSelectedAttributeOptionIdsChange,
}: AttributeFilterSectionProps) {
    function handleSelectionChange(options: SurveyAttribute['options']) {
        onSelectedAttributeOptionIdsChange(options.map((o) => o.id));
    }

    return (
        <AttributeFilter
            attributes={attributeSettings}
            selectedOptionIds={selectedAttributeOptionIds}
            onSelectionChange={handleSelectionChange}
        />
    );
}

type ChartCanvasProps = {
    scores: SurveyScoresByCategoryXAttributes;
    selectedMetric: MetricOption;
};

function ChartCanvas({ scores, selectedMetric }: ChartCanvasProps) {
    const data: ChartData<'line'> = useMemo(() => {
        const { categories, all } = scores;
        const labels = categories.map((c) => c.name);

        const baselineDataset = {
            label: BASELINE_LABEL,
            data: Array(categories.length).fill(50),
            borderColor: 'black',
            backgroundColor: 'black',
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
            spanGaps: true,
        };

        const overallDataset = {
            label: `全体 (${all.responderCount}名)`,
            data: all.categories.map((cat) => cat[selectedMetric]),
            borderColor: 'black',
            backgroundColor: 'black',
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
            spanGaps: false,
        };

        const hasAttributes = categories.some((c) => c.attributes !== null);

        if (!hasAttributes) {
            return { labels, datasets: [baselineDataset, overallDataset] };
        }

        const attributeOptionProducts = new Map<string, AttributeProductScores[]>();

        for (const category of categories) {
            if (!category.attributes) continue;

            for (const attribute of category.attributes) {
                const productLabel = attribute.product.map((p) => p.name).join(' / ');

                attributeOptionProducts.set(productLabel, [
                    ...(attributeOptionProducts.get(productLabel) ?? []),
                    attribute,
                ]);
            }
        }

        const attributeDatasets = [...attributeOptionProducts.entries()].map(([productLabel, products], colorIdx) => {
            const responderCount = Math.max(...products.map((p) => p.responderCount));
            const data = products.map((p) => p[selectedMetric]);

            return {
                label: `${productLabel} (${responderCount}名)`,
                data,
                borderColor: attributeColors[colorIdx % attributeColors.length] ?? 'grey',
                backgroundColor: attributeColors[colorIdx % attributeColors.length] ?? 'grey',
                borderDash: [],
                borderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                tension: 0,
                spanGaps: false,
            };
        });

        return { labels, datasets: [baselineDataset, overallDataset, ...attributeDatasets] };
    }, [scores, selectedMetric]);

    const chartRef = useRef<ChartJS<'line'>>(null);

    /*
     * Chart.js sizes the canvas from its container and keeps that size until it
     * is told otherwise, so printing would use the width measured on screen.
     * `resize` is given explicit dimensions for print and re-measures afterwards.
     * https://www.chartjs.org/docs/latest/configuration/responsive.html#printing-resizable-charts
     */
    useEffect(() => {
        const applyPrintSize = () => chartRef.current?.resize(PRINT_CHART_SIZE.width, PRINT_CHART_SIZE.height);
        const restoreScreenSize = () => chartRef.current?.resize();

        const printMediaQuery = window.matchMedia('print');
        const handlePrintChange = (event: MediaQueryListEvent) => {
            if (event.matches) {
                applyPrintSize();
            } else {
                restoreScreenSize();
            }
        };

        printMediaQuery.addEventListener('change', handlePrintChange);
        window.addEventListener('beforeprint', applyPrintSize);
        window.addEventListener('afterprint', restoreScreenSize);

        return () => {
            printMediaQuery.removeEventListener('change', handlePrintChange);
            window.removeEventListener('beforeprint', applyPrintSize);
            window.removeEventListener('afterprint', restoreScreenSize);
        };
    }, []);

    const options: ChartOptions<'line'> = useMemo(
        () => ({
            responsive: true,
            interaction: { mode: 'index', intersect: false },
            scales: {
                y: { grid: { color: '#e5e7eb' } },
                x: { grid: { color: '#e5e7eb' } },
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        filter: (item) => item.text !== BASELINE_LABEL,
                    },
                },
                tooltip: {
                    filter: (item) => item.dataset.label !== BASELINE_LABEL,
                    callbacks: {
                        label: (item) =>
                            `${item.dataset.label}：${item.parsed.y ? formatNumber(item.parsed.y) : 'N/A'}`,
                    },
                },
                ...customCanvasBackgroundColorOptions,
            },
        }),
        []
    );

    return <Line ref={chartRef} data={data} options={options} plugins={[customCanvasBackgroundColorPlugin]} />;
}

function ChartCanvasSkeleton() {
    return <Skeleton.Box width="100%" height={300} />;
}

export function SurveyCategoryXAttributesComparisonChartSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6" align="start">
                <Flex direction="column" gap="2" fullWidth>
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Skeleton.Box width={120} height={42} />

                <AttributeFilterSkeleton />

                <ChartCanvasSkeleton />
            </Flex>
        </Section>
    );
}
